/* istanbul ignore file */
import React from 'react';
import { InfoOutlineIcon } from '@chakra-ui/icons';
import { Box } from '@chakra-ui/react';
import { AmountType, CustomFeeAmountType, SystemTranslationKeys } from '@common/entities';

import { useSystemTranslation } from 'hooks';
import { AllChargeDetail } from 'types/shopping-cart';
import {
  appendFrequency,
  formatCustomFeeAmount,
  getLeaseChargeFrequencyTranslationKey,
  translateChargeDetailText,
} from 'utils/charge-utils';
import { formatCurrency } from 'utils/format';

import FeeTransparencyPopover from './FeeTransparencyPopover';

// Line items are a fixed two-column row: the name keeps the left half
// (left-aligned, ellipsized past its half) and the amount keeps the right half
// (right-aligned, wrapping within its half). The dotted leader is drawn full
// width behind the row; the name and amount are painted opaquely on top, so the
// visible dots run from the name to the start of the amount's first line while
// the amount stays flush-right even when it wraps. Totals span their natural
// width so the total never wraps.
const CART_COLUMN_MAX_WIDTH = '50%';
// Opaque background used to mask the leader behind the name and amount. Uses the
// Chakra body-background semantic token so it always tracks the actual cart
// surface (white in light mode) instead of a hardcoded literal, leaving only the
// dots between the two columns visible.
const CART_SURFACE_BG = 'chakra-body-bg';

interface Props {
  label: string;
  priceNumber: number;
  currencyCode?: string;
  total?: boolean;
  isRange?: boolean;
  minPriceNumber?: number;
  maxPriceNumber?: number;
  amountType?: AmountType;
  hasVaries?: boolean;
  customFeeAmountType?: CustomFeeAmountType;
  amountDetail?: string;
  // eslint-disable-next-line react/no-unused-prop-types
  priceFormatted?: string;
  // eslint-disable-next-line react/no-unused-prop-types
  minPriceFormatted?: string;
  // eslint-disable-next-line react/no-unused-prop-types
  maxPriceFormatted?: string;

  allChargesDetails?: AllChargeDetail[];
  taxAmount?: number;
  arCodeName?: string;
  arTriggerId?: number;
}

function CartItem({
  label,
  priceNumber,
  currencyCode,
  total = false,
  isRange = false,
  minPriceNumber,
  maxPriceNumber,
  amountType,
  hasVaries = false,
  customFeeAmountType,
  amountDetail,
  allChargesDetails,
  arCodeName,
  arTriggerId,
  taxAmount = 0,
  // priceFormatted, minPriceFormatted, maxPriceFormatted are intentionally unused
  // due to a known bug - we always format from the numeric values instead
}: Props) {
  const { tSystem } = useSystemTranslation();

  // Per-charge frequency ("Per Month", "One-Time") shown inline on every line
  // item; totals never carry a frequency.
  const frequencyTranslationKey = getLeaseChargeFrequencyTranslationKey(arTriggerId);
  let perChargeFrequency: string | undefined;
  if (!total) {
    perChargeFrequency = frequencyTranslationKey
      ? tSystem(frequencyTranslationKey)
      : translateChargeDetailText(tSystem, 'frequency', allChargesDetails?.[0]?.frequency);
  }

  // Non-numeric custom fees ("Varies by Usage Text", "Custom Text") have no
  // meaningful currency amount; compose their display text instead of "$0.00".
  const customFeeText = formatCustomFeeAmount({
    amountType: customFeeAmountType,
    amountDetail,
    frequency: perChargeFrequency,
    variesByUsageLabel: tSystem(SystemTranslationKeys.cart.amountVariesByUsage),
  });

  let priceToDisplay: string;
  // As of 2/20/24, the formatted price is not correct, see https://entrata.slack.com/archives/C062N6CBD61/p1708453746146959?thread_ts=1708449814.504279&cid=C062N6CBD61
  // For now, we will always format the price using the currency code and not rely on the formatted price.
  if (customFeeText !== undefined) {
    // Rich non-numeric custom fee ("Amount Varies By Usage", custom text) + frequency.
    priceToDisplay = customFeeText;
  } else if (amountType === AmountType.VariesAmount) {
    // Fallback for a varies fee with no rich metadata: the literal "Varies".
    priceToDisplay = tSystem(SystemTranslationKeys.cart.varies);
  } else if (isRange && minPriceNumber !== undefined && maxPriceNumber !== undefined) {
    const minFormatted = formatCurrency(minPriceNumber + taxAmount, currencyCode);
    const maxFormatted = formatCurrency(maxPriceNumber + taxAmount, currencyCode);
    priceToDisplay = appendFrequency(`${minFormatted}–${maxFormatted}`, perChargeFrequency);
  } else {
    priceToDisplay = appendFrequency(
      formatCurrency(priceNumber + taxAmount, currencyCode),
      perChargeFrequency,
    );
  }

  // Totals append a localized "- Varies" suffix when any contributing charge varies.
  if (hasVaries) {
    priceToDisplay = `${priceToDisplay} - ${tSystem(SystemTranslationKeys.cart.varies)}`;
  }

  // Check if this is an average type fee
  const isAverageType = amountType === AmountType.AverageAmount;

  const hasMultipleChargeDetails = (allChargesDetails?.length ?? 0) > 1;
  const labelToUse = hasMultipleChargeDetails ? (arCodeName ?? label) : label;
  const averageSuffix = isAverageType ? ` (${tSystem(SystemTranslationKeys.cart.average)})` : '';
  const displayLabel = `${labelToUse}${averageSuffix}`;
  const firstChargeDetail = allChargesDetails?.[0];
  const popoverCharges = firstChargeDetail
    ? [{ ...firstChargeDetail, frequency: perChargeFrequency ?? firstChargeDetail.frequency }]
    : [];
  const shouldShowFeeTransparency = popoverCharges.length > 0;

  return (
    <Box
      position="relative"
      display="flex"
      justifyContent="space-between"
      alignItems="baseline"
      minW={0}
      py="2px"
      lineHeight="1.5"
      fontSize="13px"
      fontStyle="normal"
      fontWeight={total ? 'bold' : 'normal'}
      color="gray.drakest"
      bg={CART_SURFACE_BG}
    >
      {/* Full-width dotted leader sitting on the first line. The name and amount
          are painted opaquely on top, so the visible dots run from the name to
          the start of the amount's first line and the amount stays flush-right
          even when it wraps to a second line. */}
      <Box
        aria-hidden
        position="absolute"
        left={0}
        right={0}
        top="2px"
        h="1.15em"
        borderBottomWidth="2px"
        borderBottomStyle="dotted"
        borderColor="gray.base"
        pointerEvents="none"
      />
      <Box
        display="flex"
        alignItems="center"
        minW={0}
        flexShrink={1}
        maxW={total ? undefined : CART_COLUMN_MAX_WIDTH}
        position="relative"
        zIndex={1}
        bg={CART_SURFACE_BG}
        pr="6px"
      >
        <Box
          as="span"
          title={displayLabel}
          minW="0"
          overflow="hidden"
          textOverflow="ellipsis"
          whiteSpace="nowrap"
        >
          {displayLabel}
        </Box>
        {shouldShowFeeTransparency && (
          <FeeTransparencyPopover label={displayLabel} charges={popoverCharges}>
            <InfoOutlineIcon ml={2} />
          </FeeTransparencyPopover>
        )}
      </Box>
      <Box
        position="relative"
        zIndex={1}
        flexShrink={total ? 0 : 1}
        minW={total ? undefined : 0}
        maxW={total ? undefined : CART_COLUMN_MAX_WIDTH}
        textAlign={total ? undefined : 'right'}
        whiteSpace={total ? undefined : 'normal'}
        overflowWrap={total ? undefined : 'break-word'}
      >
        <Box
          as="span"
          bg={CART_SURFACE_BG}
          pl="6px"
          sx={{ boxDecorationBreak: 'clone', WebkitBoxDecorationBreak: 'clone' }}
        >
          {priceToDisplay}
        </Box>
      </Box>
    </Box>
  );
}

export default CartItem;
